import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CreateSurveyResponse,
  FetchHistorySurveyParams,
  FetchSurveyParams,
  SubmitSurveyResponse,
  Survey,
  SurveyListResponse,
} from "../types/surveyTypes";
import {
  createSurveyAPI,
  deleteSurvey,
  fetchHistorySurveyList,
  fetchSurveyDetails,
  fetchSurveyList,
  getSubmitSurveyDetails,
  updateSurvey,
} from "../services/surveyServices";

// survey Listing
export const fetchAllSurveys = createAsyncThunk<
  SurveyListResponse,
  FetchSurveyParams,
  { rejectValue: string }
>("survey/fetchAll", async (params, { rejectWithValue }) => {
  try {
    return await fetchSurveyList(params);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch survey list");
  }
});

// survey Delete
export const deleteSurveyById = createAsyncThunk<
  SurveyListResponse,
  number,
  { rejectValue: string }
>("survey/delete", async (id, { rejectWithValue }) => {
  try {
    return await deleteSurvey(id);
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to delete user");
  }
});

// survey history list
export const fetchHistoryAllSurveys = createAsyncThunk<
  SurveyListResponse,
  FetchHistorySurveyParams,
  { rejectValue: string }
>("survey/fetchSurveyHistory", async (params, { rejectWithValue }) => {
  try {
    return await fetchHistorySurveyList(params);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch survey list");
  }
});

// survey Create
export const createSurvey = createAsyncThunk<
  CreateSurveyResponse, // ✅ Return type
  FormData, // ✅ Argument
  { rejectValue: string } // ✅ Error type
>("survey/createProject", async (formData, { rejectWithValue }) => {
  try {
    const response = await createSurveyAPI(formData);
    return response;
  } catch (error: any) {
    return rejectWithValue(error || "Failed to create project");
  }
});

// update Survey
export const updateSurveyById = createAsyncThunk<
  CreateSurveyResponse,
  { id: number; formData: FormData },
  { rejectValue: string }
>("survey/update", async ({ id, formData }, { rejectWithValue }) => {
  try {
    const res = await updateSurvey(id, formData); // ⬅️ From your service
    return res;
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to update survey");
  }
});

// get survey Details
export const getSurveyDetails = createAsyncThunk<
  CreateSurveyResponse,
  { id: number },
  { rejectValue: string }
>("survey/getDetails", async ({ id }, { rejectWithValue }) => {
  try {
    const res = await fetchSurveyDetails(id);
    return res;
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to fetch survey");
  }
});
// get submit survey by id
export const fetchSubmitSurveyById = createAsyncThunk<
  SubmitSurveyResponse,
  number,
  { rejectValue: string }
>("survey/fetchSubmitSurvey", async (submission_id, { rejectWithValue }) => {
  try {
    return await getSubmitSurveyDetails(submission_id);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch submit survey");
  }
});

interface SurveyState {
  loading: boolean;
  surveyDetailsLoading: boolean;
  error: string | null;
  surveyList: Survey[];
  total: number;
  surveyDetails: Survey | null;
  submitSurveyData: SubmitSurveyResponse | null;
}

const initialState: SurveyState = {
  loading: false,
  error: null,
  surveyList: [],
  total: 0,
  surveyDetails: null,
  submitSurveyData: null,
  surveyDetailsLoading: false,
};

const surveySlice = createSlice({
  name: "survey",
  initialState,
  reducers: {
    resetSurveyState: (state) => {
      state.loading = false;
      state.error = null;
      state.surveyList = [];
      state.total = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // create survey
      .addCase(createSurvey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSurvey.fulfilled, (state, action) => {
        state.loading = false;
        state.surveyList = [action.payload.data, ...state.surveyList]; // Optionally prepend
      })
      .addCase(createSurvey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create survey";
      })

      // fetch all survey
      .addCase(fetchAllSurveys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllSurveys.fulfilled, (state, action) => {
        state.loading = false;
        state.surveyList = action.payload.data.data;
        state.total = action.payload.data.total;
      })
      .addCase(fetchAllSurveys.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch survey list";
      })
      // delete survey
      .addCase(deleteSurveyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSurveyById.fulfilled, (state, action) => {
        const deletedId = action.meta.arg;
        state.surveyList = state.surveyList.filter(
          (survey) => survey.id !== deletedId,
        );
        state.loading = false;
      })

      .addCase(deleteSurveyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to Delete role";
      })
      // surveyhistory list
      .addCase(fetchHistoryAllSurveys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHistoryAllSurveys.fulfilled, (state, action) => {
        state.loading = false;
        state.surveyList = action.payload.data.data;
        state.total = action.payload.data.total;
      })
      .addCase(fetchHistoryAllSurveys.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch survey list";
      })
      // get survey detail
      .addCase(getSurveyDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSurveyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.surveyDetails = action.payload.data; // ✅ Store the fetched survey details
      })
      .addCase(getSurveyDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch survey";
      })
      // update survey
      .addCase(updateSurveyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSurveyById.fulfilled, (state, action) => {
        state.loading = false;
        const updatedSurvey = action.payload.data;
        state.surveyList = state.surveyList.map((survey) =>
          survey.id === updatedSurvey.id
            ? { ...survey, ...updatedSurvey }
            : survey,
        );

        // Also update surveyDetails if it matches the updated one
        if (state.surveyDetails?.id === updatedSurvey.id) {
          state.surveyDetails = { ...state.surveyDetails, ...updatedSurvey };
        }
      })

      .addCase(updateSurveyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create survey";
      })
      // fetch submit survey by id
      .addCase(fetchSubmitSurveyById.pending, (state) => {
        state.surveyDetailsLoading = true;
        state.error = null;
      })
      .addCase(fetchSubmitSurveyById.fulfilled, (state, action) => {
        state.surveyDetailsLoading = false;
        state.submitSurveyData = action.payload;
      })
      .addCase(fetchSubmitSurveyById.rejected, (state, action) => {
        state.surveyDetailsLoading = false;
        state.error = action.payload || "Failed to fetch submit survey";
      });
  },
});

export const { resetSurveyState } = surveySlice.actions;
export default surveySlice.reducer;
